import type { MeasurementStatus, ParameterMeasurement } from '../hooks/useLatestMeasurements';

/** Overall verdict shown next to the score. `none` = no data to score. */
export type HealthLevel = 'excellent' | 'good' | 'fair' | 'poor' | 'none';

export interface HealthScoreResult {
  /** 0–100, or null when no parameter has a value with a known target range. */
  readonly score: number | null;
  readonly level: HealthLevel;
  /** Parameters that contributed to the score. */
  readonly scoredCount: number;
  /** Parameters the score is meant to cover. */
  readonly totalCount: number;
  /** True when some, but not all, parameters contributed. */
  readonly partial: boolean;
}

/** Points per parameter status. `unknown` (no target range) never contributes. */
const STATUS_POINTS: ReadonlyMap<MeasurementStatus, number> = new Map<MeasurementStatus, number>([
  ['excellent', 100],
  ['stable', 80],
  ['warning', 50],
  ['critical', 15],
]);

export function healthLevelFor(score: number | null): HealthLevel {
  if (score === null) return 'none';
  if (score >= 90) return 'excellent';
  if (score >= 75) return 'good';
  if (score >= 50) return 'fair';
  return 'poor';
}

/**
 * Health score from the latest water values: the mean of the per-parameter
 * points of every parameter that has a value. Never invents a score — with no
 * data the result is `score: null` / `level: 'none'`; with partial data it is
 * computed from what is available and flagged `partial`.
 */
export function computeHealthScore(
  measurements: Readonly<Record<string, ParameterMeasurement | null>>,
  parameters: readonly string[],
): HealthScoreResult {
  const points: number[] = [];
  for (const param of parameters) {
    const measurement = Object.prototype.hasOwnProperty.call(measurements, param) ? measurements[param] : null;
    if (measurement == null) continue;
    const value = STATUS_POINTS.get(measurement.status);
    if (value !== undefined) points.push(value);
  }
  const score = points.length === 0 ? null : Math.round(points.reduce((sum, p) => sum + p, 0) / points.length);
  return {
    score,
    level: healthLevelFor(score),
    scoredCount: points.length,
    totalCount: parameters.length,
    partial: points.length > 0 && points.length < parameters.length,
  };
}
